import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw,
  Edit,
  CheckSquare,
  Square,
  ChevronUp,
  ChevronDown,
  Filter,
  X,
  Plus,
  Save
} from 'lucide-react';
import { useAuth } from '../../../../../../contexts/AuthContext';
import { itemsService } from '../../../../../../services/itemsService';
import { stcBinGrpService } from '../../../../../../services/stcBinGrpService';
import reportConfigurationService from '../../../../../../services/reportConfigurationService';
import { stcMovementTypeService } from '../../../../../../services/stcMovementTypeService';
import { stcSortingBoxDefService } from '../../../../../../services/stcSortingBoxDefService';
import { ItemsDto, CreateItemsRequest, UpdateItemsRequest, EnrichedItem } from '../../../../../../types/items';
import {
  MasterDataLookups,
  ColumnFilters,
  SortConfig,
  ColumnDefinition,
  AdvancedFilters
} from './types';
import { ItemsBulkUpdateModal } from './ItemsBulkUpdateModal';

export const ItemsDataTable: React.FC = () => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // Core data state
  const [items, setItems] = useState<ItemsDto[]>([]);
  const [enrichedItems, setEnrichedItems] = useState<EnrichedItem[]>([]);
  const [masterData, setMasterData] = useState<MasterDataLookups | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItemNos, setSelectedItemNos] = useState<string[]>([]);

  // Filter state
  const [filters, setFilters] = useState<Partial<ColumnFilters>>({});
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({});
  const [showFilters, setShowFilters] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Bulk edit modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  // Sort state
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'itemNo', direction: 'asc' });

  // Edit state
  const [editingItemNo, setEditingItemNo] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Partial<ItemsDto>>({});
  const [showCreateRow, setShowCreateRow] = useState(false);
  const [createData, setCreateData] = useState<Partial<CreateItemsRequest>>({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  /**
   * Load all items and master data on mount
   */
  useEffect(() => {
    loadData();
  }, [companyNo]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch items and all related master data in parallel
      const [
        itemsData,
        binGroupsData,
        reportsData,
        movementTypesData,
        sortingBoxesData
      ] = await Promise.all([
        itemsService.getCurrentItems(),
        stcBinGrpService.getCurrent(),
        reportConfigurationService.getAll(companyNo),
        stcMovementTypeService.getCurrent(),
        stcSortingBoxDefService.getCurrent()
      ]);

      // Create hardcoded pick rules lookup
      const pickRulesMap = new Map<number, { pickRuleNo: number; description: string }>([
        [1, { pickRuleNo: 1, description: 'FIFO' }],
        [2, { pickRuleNo: 2, description: 'Antall' }],
        [3, { pickRuleNo: 3, description: 'Prioritet' }],
        [4, { pickRuleNo: 4, description: 'Batch fra ERP' }]
      ]);

      // Create hardcoded item types lookup
      const itemTypesMap = new Map<number, { itemType: number; description: string }>([
        [0, { itemType: 0, description: 'Standard' }],
        [1, { itemType: 1, description: 'Batchnr' }],
        [2, { itemType: 2, description: 'Serienummer' }],
        [3, { itemType: 3, description: 'Stand005' }],
        [4, { itemType: 4, description: 'Batch med holdbarhet' }]
      ]);

      // Create lookup maps for O(1) foreign key resolution
      const lookups: MasterDataLookups = {
        binGrps: new Map(binGroupsData.map(g => [g.binGrpNo, g])),
        reports: new Map(reportsData.map(r => [r.reportID, r])),
        movementTypes: new Map(movementTypesData.map(m => [m.movementTypeNo, m])),
        sortingBoxes: new Map(sortingBoxesData.map(s => [s.stcBoxTypeID, s])),
        pickRules: pickRulesMap,
        itemTypes: itemTypesMap
      };

      // Enrich items with related entity descriptions
      const enriched: EnrichedItem[] = itemsData.map(item => ({
        ...item,
        binGrpDescription: item.binGrpNo ? lookups.binGrps.get(item.binGrpNo)?.description1 : undefined,
        reportName: item.reportID ? lookups.reports.get(item.reportID)?.reportName : undefined,
        itemTypeText: item.itemType !== undefined ? lookups.itemTypes.get(item.itemType)?.description : undefined,
        pickRuleText: item.pickRuleNo ? lookups.pickRules.get(item.pickRuleNo)?.description : undefined,
        movementTypeDescription: item.movementTypeNo ? lookups.movementTypes.get(item.movementTypeNo)?.description1 : undefined,
        stcBoxTypeDescription: item.stcBoxTypeID ? lookups.sortingBoxes.get(item.stcBoxTypeID)?.description1 : undefined
      }));

      setItems(itemsData);
      setEnrichedItems(enriched);
      setMasterData(lookups);

    } catch (err) {
      console.error('Error loading items data:', err);
      setError('Kunne ikke laste varedata. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [companyNo]);

  /**
   * Wildcard matching function
   */
  const matchWildcard = useCallback((text: string, pattern: string): boolean => {
    if (!pattern) return true;

    // Convert wildcard pattern to regex
    let regexPattern = pattern
      .replace(/[.+^${}()|[\]\\]/g, '\\$&')  // Escape regex special chars
      .replace(/\*/g, '.*')                   // * becomes .* (0 or more chars)
      .replace(/\?/g, '.');                   // ? becomes . (exactly one char)

    const regex = new RegExp(`^${regexPattern}$`, 'i');
    return regex.test(text);
  }, []);

  /**
   * Apply advanced filter to a single value
   */
  const applyAdvancedFilter = useCallback((value: any, filter: any): boolean => {
    const strValue = String(value || '').toLowerCase();
    const filterValue = String(filter.value || '').toLowerCase();

    switch (filter.type) {
      case 'contains':
        return strValue.includes(filterValue);
      case 'notContains':
        return !strValue.includes(filterValue);
      case 'equals':
        return strValue === filterValue;
      case 'notEquals':
        return strValue !== filterValue;
      case 'startsWith':
        return strValue.startsWith(filterValue);
      case 'endsWith':
        return strValue.endsWith(filterValue);
      case 'wildcard':
        return matchWildcard(strValue, filterValue);
      case 'greaterThan':
        if (typeof value === 'number') {
          return value > parseFloat(filter.value);
        }
        return strValue > filterValue;
      case 'lessThan':
        if (typeof value === 'number') {
          return value < parseFloat(filter.value);
        }
        return strValue < filterValue;
      case 'between':
        if (filter.valueTo) {
          if (typeof value === 'number') {
            const from = parseFloat(filter.value);
            const to = parseFloat(filter.valueTo);
            return value >= from && value <= to;
          }
          return strValue >= filterValue && strValue <= filter.valueTo.toLowerCase();
        }
        return true;
      default:
        return true;
    }
  }, [matchWildcard]);

  /**
   * Apply filters and sorting with pagination
   */
  const { filteredAndSortedItems, paginatedItems, totalPages, totalRows } = useMemo(() => {
    let result = [...enrichedItems];

    // Apply simple filters (from inline filter inputs)
    Object.entries(filters).forEach(([key, value]) => {
      if (!value || value === 'all') return;

      result = result.filter(item => {
        const itemValue = item[key as keyof EnrichedItem];

        // Boolean filters
        if (value === 'yes') return itemValue === true;
        if (value === 'no') return itemValue === false || itemValue === undefined;

        // Text/number filters (case-insensitive partial match)
        if (typeof value === 'string' && value.trim()) {
          const filterStr = value.toLowerCase();
          const itemStr = String(itemValue || '').toLowerCase();
          return itemStr.includes(filterStr);
        }

        return true;
      });
    });

    // Apply advanced filters
    Object.entries(advancedFilters).forEach(([key, filter]) => {
      if (!filter || !filter.value) return;

      result = result.filter(item => {
        const itemValue = item[key as keyof EnrichedItem];
        return applyAdvancedFilter(itemValue, filter);
      });
    });

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key as keyof EnrichedItem];
        const bVal = b[sortConfig.key as keyof EnrichedItem];

        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        const comparison = aVal < bVal ? -1 : 1;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    // Calculate pagination
    const totalRows = result.length;
    const totalPages = Math.ceil(totalRows / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = result.slice(startIndex, endIndex);

    return {
      filteredAndSortedItems: result,
      paginatedItems,
      totalPages,
      totalRows
    };
  }, [enrichedItems, filters, advancedFilters, sortConfig, currentPage, pageSize, applyAdvancedFilter]);

  /**
   * Reset to page 1 when filters change
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, advancedFilters]);

  /**
   * Handle column sort
   */
  const handleSort = useCallback((key: keyof EnrichedItem) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  /**
   * Handle filter change
   */
  const handleFilterChange = useCallback((columnKey: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({});
    setAdvancedFilters({});
  }, []);

  /**
   * Handle checkbox selection
   */
  const handleCheckboxChange = useCallback((itemNo: string, checked: boolean) => {
    if (checked) {
      setSelectedItemNos(prev => [...prev, itemNo]);
    } else {
      setSelectedItemNos(prev => prev.filter(no => no !== itemNo));
    }
  }, []);

  /**
   * Handle select all / clear selection (only current page)
   */
  const handleSelectAll = useCallback(() => {
    if (selectedItemNos.length === paginatedItems.length && paginatedItems.every(i => selectedItemNos.includes(i.itemNo))) {
      setSelectedItemNos([]);
    } else {
      setSelectedItemNos(paginatedItems.map(i => i.itemNo));
    }
  }, [selectedItemNos, paginatedItems]);

  /**
   * Start inline edit for a row
   */
  const startEdit = useCallback((item: ItemsDto) => {
    setEditingItemNo(item.itemNo);
    setEditingData({ ...item });
  }, []);

  /**
   * Cancel inline edit
   */
  const cancelEdit = useCallback(() => {
    setEditingItemNo(null);
    setEditingData({});
  }, []);

  /**
   * Save inline edit
   */
  const saveEdit = useCallback(async () => {
    if (!editingItemNo || !editingData.companyNo) {
      return;
    }

    try {
      setIsLoading(true);

      const updateRequest: UpdateItemsRequest = {
        description1: editingData.description1,
        itemBarCode: editingData.itemBarCode,
        supplierItemNo: editingData.supplierItemNo,
        binGrpNo: editingData.binGrpNo,
        pickRuleNo: editingData.pickRuleNo,
        stcBoxTypeID: editingData.stcBoxTypeID,
        putAwayToPickableBinYesNo: editingData.putAwayToPickableBinYesNo,
        movementTypeNo: editingData.movementTypeNo,
        stcTransactionsYesNo: editingData.stcTransactionsYesNo,
        newLineYesNo: editingData.newLineYesNo,
        itemGrp1: editingData.itemGrp1,
        itemGrp2: editingData.itemGrp2,
        itemGrp3: editingData.itemGrp3,
        freeTxt1: editingData.freeTxt1,
        freeTxt2: editingData.freeTxt2,
        freeTxt3: editingData.freeTxt3,
        freeTxt4: editingData.freeTxt4,
        freeTxt5: editingData.freeTxt5,
        freeTxt6: editingData.freeTxt6,
        freeTxt7: editingData.freeTxt7,
        freeTxt8: editingData.freeTxt8,
        freeTxt9: editingData.freeTxt9,
        freeTxt10: editingData.freeTxt10,
        itemPictureName: editingData.itemPictureName,
        stdLocationName: editingData.stdLocationName,
        reportID: editingData.reportID,
        stdBinCode: editingData.stdBinCode,
        useSharedBinYesNo: editingData.useSharedBinYesNo,
        useMaxReplenishQtyYesNo: editingData.useMaxReplenishQtyYesNo,
        maxReplenishQty: editingData.maxReplenishQty
      };

      await itemsService.updateItem(companyNo, editingItemNo, updateRequest);

      setEditingItemNo(null);
      setEditingData({});
      await loadData();
    } catch (err) {
      console.error('Error updating item:', err);
      alert('Kunne ikke oppdatere vare. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [editingItemNo, editingData, companyNo, loadData]);

  /**
   * Update editing field value
   */
  const updateEditingField = useCallback((field: keyof ItemsDto, value: any) => {
    setEditingData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  /**
   * Handle create new item
   */
  const handleCreateNew = useCallback(() => {
    setShowCreateRow(true);
    setCreateData({
      companyNo: companyNo,
      itemNo: '',
      description1: '',
      stcTransactionsYesNo: true
    });
  }, [companyNo]);

  /**
   * Cancel create new
   */
  const cancelCreate = useCallback(() => {
    setShowCreateRow(false);
    setCreateData({});
  }, []);

  /**
   * Save new item
   */
  const saveCreate = useCallback(async () => {
    if (!createData.itemNo || !createData.companyNo) {
      alert('Varenr. er påkrevd');
      return;
    }

    try {
      setIsLoading(true);
      await itemsService.createItem(createData as CreateItemsRequest);

      setShowCreateRow(false);
      setCreateData({});
      await loadData();
    } catch (err: any) {
      console.error('Error creating item:', err);
      alert('Kunne ikke opprette vare: ' + (err.message || 'Prøv igjen.'));
    } finally {
      setIsLoading(false);
    }
  }, [createData, loadData]);

  /**
   * Update create field value
   */
  const updateCreateField = useCallback((field: keyof CreateItemsRequest, value: any) => {
    setCreateData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  /**
   * Handle bulk delete
   */
  const handleBulkDelete = useCallback(async () => {
    if (selectedItemNos.length === 0) {
      window.alert('Velg minst én vare for å slette');
      return;
    }

    if (!window.confirm(`Er du sikker på at du vil slette ${selectedItemNos.length} vare(r)?`)) {
      return;
    }

    try {
      setIsLoading(true);

      for (const itemNo of selectedItemNos) {
        await itemsService.deleteItem(companyNo, itemNo);
      }

      setSelectedItemNos([]);
      await loadData();
      alert(`${selectedItemNos.length} vare(r) slettet`);
    } catch (err) {
      console.error('Error deleting items:', err);
      alert('Kunne ikke slette varer. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedItemNos, companyNo, loadData]);

  /**
   * Handle bulk edit button click
   */
  const handleBulkEdit = useCallback(() => {
    if (selectedItemNos.length === 0) {
      window.alert('Velg minst én vare for å redigere');
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedItemNos.length]);

  /**
   * Handle bulk update success
   */
  const handleUpdateSuccess = useCallback(() => {
    loadData();
    setSelectedItemNos([]);
    setShowBulkEditModal(false);
  }, [loadData]);

  /**
   * Handle bulk delete success from modal
   */
  const handleDeleteSuccess = useCallback(() => {
    loadData();
    setSelectedItemNos([]);
    setShowBulkEditModal(false);
  }, [loadData]);

  // Column definitions
  const columns: ColumnDefinition[] = [
    { key: 'actions', header: 'Handlinger', width: '100px', type: 'text', editable: false, sortable: false, filterable: false },
    { key: 'itemNo', header: 'Varenr.', width: '120px', type: 'text', editable: false, sortable: true, filterable: true },
    { key: 'description1', header: 'Beskrivelse', width: '200px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'itemBarCode', header: 'GTIN', width: '150px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'supplierItemNo', header: 'Levr. varenr.', width: '150px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'binGrpNo', header: 'Lokasjonsgruppe', width: '180px', type: 'dropdown', editable: true, sortable: true, filterable: true },
    { key: 'pickRuleNo', header: 'Plukkregel', width: '150px', type: 'dropdown', editable: true, sortable: true, filterable: true },
    { key: 'stcBoxTypeID', header: 'Sorteringskasse', width: '150px', type: 'dropdown', editable: true, sortable: true, filterable: true },
    { key: 'putAwayToPickableBinYesNo', header: 'Mottak til plukklok.', width: '160px', type: 'boolean', editable: true, sortable: true, filterable: true },
    { key: 'movementTypeNo', header: 'Bevegelseskode', width: '150px', type: 'dropdown', editable: true, sortable: true, filterable: true },
    { key: 'stcTransactionsYesNo', header: 'Lagerstyring', width: '120px', type: 'boolean', editable: true, sortable: true, filterable: true },
    { key: 'newLineYesNo', header: 'Tillegg J/N', width: '100px', type: 'boolean', editable: true, sortable: true, filterable: true },
    { key: 'itemGrp1', header: 'VareGrp. 1', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'itemGrp2', header: 'VareGrp. 2', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'itemGrp3', header: 'VareGrp. 3', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt1', header: 'Fritekst 1', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt2', header: 'Fritekst 2', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt3', header: 'Fritekst 3', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt4', header: 'Fritekst 4', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt5', header: 'Fritekst 5', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt6', header: 'Fritekst 6', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt7', header: 'Fritekst 7', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt8', header: 'Fritekst 8', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt9', header: 'Fritekst 9', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'freeTxt10', header: 'Fritekst 10', width: '120px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'itemPictureName', header: 'Bildenavn', width: '150px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'stdLocationName', header: 'Standard Lok.', width: '150px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'reportID', header: 'Standard etikett', width: '180px', type: 'dropdown', editable: true, sortable: true, filterable: true },
    { key: 'itemType', header: 'Varetype', width: '180px', type: 'text', editable: false, sortable: true, filterable: true },
    { key: 'stdBinCode', header: 'Fast Plukklokasjon', width: '150px', type: 'text', editable: true, sortable: true, filterable: true },
    { key: 'useSharedBinYesNo', header: 'Kan brukes på felles lok.', width: '180px', type: 'boolean', editable: true, sortable: true, filterable: true },
    { key: 'useMaxReplenishQtyYesNo', header: 'Bruk maks påfylling', width: '160px', type: 'boolean', editable: true, sortable: true, filterable: true },
    { key: 'maxReplenishQty', header: 'Maks påfyllingsantall', width: '160px', type: 'number', editable: true, sortable: true, filterable: true }
  ];

  /**
   * Render cell value (for non-edit mode)
   */
  const renderCellValue = (item: EnrichedItem, column: ColumnDefinition) => {
    // Show enriched names for lookup fields in readonly mode
    if (column.key === 'binGrpNo' && item.binGrpDescription) {
      return `${item.binGrpNo} - ${item.binGrpDescription}`;
    }
    if (column.key === 'reportID' && item.reportName) {
      return item.reportName;
    }
    if (column.key === 'itemType' && item.itemTypeText) {
      return item.itemTypeText;
    }
    if (column.key === 'pickRuleNo' && item.pickRuleText) {
      return `${item.pickRuleNo} - ${item.pickRuleText}`;
    }
    if (column.key === 'movementTypeNo' && item.movementTypeDescription) {
      return `${item.movementTypeNo} - ${item.movementTypeDescription}`;
    }
    if (column.key === 'stcBoxTypeID' && item.stcBoxTypeDescription) {
      return `${item.stcBoxTypeID} - ${item.stcBoxTypeDescription}`;
    }

    const value = item[column.key as keyof EnrichedItem];

    if (column.type === 'boolean') {
      return value === true ? 'Ja' : value === false ? 'Nei' : '-';
    }

    if (column.type === 'date' && value) {
      return new Date(value as string).toLocaleDateString('no-NO');
    }

    return value !== null && value !== undefined ? String(value) : '-';
  };

  /**
   * Render editable cell (for edit mode)
   */
  const renderEditableCell = (column: ColumnDefinition, data: Partial<ItemsDto>, updateFn: (field: keyof ItemsDto, value: any) => void) => {
    const value = data[column.key as keyof ItemsDto];

    // Non-editable fields or actions column
    if (!column.editable || column.key === 'actions') {
      return renderCellValue(data as EnrichedItem, column);
    }

    // Dropdown fields based on master data
    if (column.key === 'binGrpNo' && masterData) {
      return (
        <select
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => updateFn('binGrpNo', e.target.value ? parseInt(e.target.value) : undefined)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Ingen</option>
          {Array.from(masterData.binGrps.values()).map(grp => (
            <option key={grp.binGrpNo} value={grp.binGrpNo}>
              {grp.binGrpNo} - {grp.description1}
            </option>
          ))}
        </select>
      );
    }

    if (column.key === 'pickRuleNo' && masterData) {
      return (
        <select
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => updateFn('pickRuleNo', e.target.value ? parseInt(e.target.value) : undefined)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Ingen</option>
          {Array.from(masterData.pickRules.values()).map(rule => (
            <option key={rule.pickRuleNo} value={rule.pickRuleNo}>
              {rule.pickRuleNo} - {rule.description}
            </option>
          ))}
        </select>
      );
    }

    if (column.key === 'stcBoxTypeID' && masterData) {
      return (
        <select
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => updateFn('stcBoxTypeID', e.target.value || undefined)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Ingen</option>
          {Array.from(masterData.sortingBoxes.values()).map(box => (
            <option key={box.stcBoxTypeID} value={box.stcBoxTypeID}>
              {box.stcBoxTypeID} - {box.description1}
            </option>
          ))}
        </select>
      );
    }

    if (column.key === 'movementTypeNo' && masterData) {
      return (
        <select
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => updateFn('movementTypeNo', e.target.value ? parseInt(e.target.value) : undefined)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Ingen</option>
          {Array.from(masterData.movementTypes.values()).map(type => (
            <option key={type.movementTypeNo} value={type.movementTypeNo}>
              {type.movementTypeNo} - {type.description1}
            </option>
          ))}
        </select>
      );
    }

    if (column.key === 'reportID' && masterData) {
      return (
        <select
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => updateFn('reportID', e.target.value ? parseInt(e.target.value) : undefined)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Ingen</option>
          {Array.from(masterData.reports.values()).map(report => (
            <option key={report.reportID} value={report.reportID}>
              {report.reportName}
            </option>
          ))}
        </select>
      );
    }

    // Boolean fields
    if (column.type === 'boolean') {
      return (
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => updateFn(column.key as keyof ItemsDto, e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
      );
    }

    // Number fields
    if (column.type === 'number') {
      return (
        <input
          type="number"
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => updateFn(column.key as keyof ItemsDto, e.target.value ? parseFloat(e.target.value) : undefined)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
        />
      );
    }

    // Text fields
    return (
      <input
        type="text"
        value={value !== null && value !== undefined ? String(value) : ''}
        onChange={(e) => updateFn(column.key as keyof ItemsDto, e.target.value)}
        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
      />
    );
  };

  /**
   * Render filter input
   */
  const renderFilterInput = (column: ColumnDefinition) => {
    if (!column.filterable || column.key === 'actions') return null;

    if (column.type === 'boolean') {
      return (
        <select
          value={filters[column.key as keyof ColumnFilters] || 'all'}
          onChange={(e) => handleFilterChange(column.key, e.target.value)}
          className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-blue-500"
        >
          <option value="all">Alle</option>
          <option value="yes">Ja</option>
          <option value="no">Nei</option>
        </select>
      );
    }

    return (
      <input
        type="text"
        value={filters[column.key as keyof ColumnFilters] || ''}
        onChange={(e) => handleFilterChange(column.key, e.target.value)}
        placeholder="Filter..."
        className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-blue-500"
      />
    );
  };

  // Loading state
  if (isLoading && !enrichedItems.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-2" size={32} />
          <p className="text-gray-600">Laster varer...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center text-red-600">
          <p className="font-semibold mb-2">Feil ved lasting av data</p>
          <p className="text-sm mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all').length;
  const activeAdvancedFilterCount = Object.keys(advancedFilters).length;
  const totalActiveFilters = activeFilterCount + activeAdvancedFilterCount;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">
            Varer ({totalRows} av {enrichedItems.length})
          </h2>
          {selectedItemNos.length > 0 && (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {selectedItemNos.length} valgt
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              window.location.hash = '';
              window.location.reload();
            }}
            className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
            title="Se den nye migrerte versjonen"
          >
            🚀 Vis MIGRATED versjon
          </button>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded flex items-center gap-2 ${
              showFilters ? 'bg-blue-600 text-white' : 'border hover:bg-gray-50'
            }`}
          >
            <Filter size={16} />
            Filtre {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>

          <button
            onClick={() => setShowAdvancedFilters(true)}
            className={`px-3 py-2 rounded flex items-center gap-2 ${
              activeAdvancedFilterCount > 0 ? 'bg-green-600 text-white' : 'border hover:bg-gray-50'
            }`}
          >
            <Filter size={16} />
            Avanserte filtre {activeAdvancedFilterCount > 0 && `(${activeAdvancedFilterCount})`}
          </button>

          {totalActiveFilters > 0 && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 border rounded hover:bg-gray-50 flex items-center gap-2"
            >
              <X size={16} />
              Tøm filtre
            </button>
          )}

          <button
            onClick={handleBulkEdit}
            disabled={selectedItemNos.length === 0}
            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Edit size={16} />
            Vedlikehold ({selectedItemNos.length})
          </button>

          <button
            onClick={handleSelectAll}
            className="px-3 py-2 border rounded hover:bg-gray-50 flex items-center gap-2"
          >
            {selectedItemNos.length === paginatedItems.length && paginatedItems.every(i => selectedItemNos.includes(i.itemNo)) ? (
              <><CheckSquare size={16} /> Fjern alle</>
            ) : (
              <><Square size={16} /> Velg alle</>
            )}
          </button>

          <button
            onClick={handleCreateNew}
            className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
          >
            <Plus size={16} />
            Opprett ny
          </button>

          <button
            onClick={handleBulkDelete}
            disabled={selectedItemNos.length === 0}
            className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <X size={16} />
            Slett ({selectedItemNos.length})
          </button>

          <button
            onClick={loadData}
            className="p-2 border rounded hover:bg-gray-50"
            title="Oppdater"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700">
            Viser {totalRows > 0 ? ((currentPage - 1) * pageSize) + 1 : 0}-{Math.min(currentPage * pageSize, totalRows)} av {totalRows}
          </span>

          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="px-3 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value={50}>50 per side</option>
            <option value={100}>100 per side</option>
            <option value={200}>200 per side</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Første
          </button>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Forrige
          </button>

          <span className="px-3 text-sm font-medium">
            Side {currentPage} av {totalPages || 1}
          </span>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className="px-3 py-1.5 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Neste
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            className="px-3 py-1.5 border rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Siste
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            {/* Filter row */}
            {showFilters && (
              <tr className="border-b">
                <th className="w-10 px-2 py-1 border-r bg-gray-100"></th>
                {columns.map(column => (
                  <th
                    key={column.key}
                    className="px-2 py-1 border-r"
                    style={{ width: column.width }}
                  >
                    {renderFilterInput(column)}
                  </th>
                ))}
              </tr>
            )}

            {/* Header row */}
            <tr className="border-b">
              <th className="w-10 px-2 py-2 border-r bg-gray-100">
                <input
                  type="checkbox"
                  checked={selectedItemNos.length === paginatedItems.length && paginatedItems.length > 0 && paginatedItems.every(i => selectedItemNos.includes(i.itemNo))}
                  onChange={handleSelectAll}
                  className="cursor-pointer"
                />
              </th>
              {columns.map(column => (
                <th
                  key={column.key}
                  className="px-2 py-2 text-left font-semibold border-r cursor-pointer hover:bg-gray-100"
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key as keyof EnrichedItem)}
                >
                  <div className="flex items-center justify-between">
                    <span>{column.header}</span>
                    {column.sortable && sortConfig.key === column.key && (
                      sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* Create new row */}
            {showCreateRow && (
              <tr className="bg-yellow-50 border-b-2 border-yellow-400">
                <td className="px-2 py-2 border-r text-center">
                  {/* No checkbox for create row */}
                </td>
                {columns.map(column => (
                  <td
                    key={column.key}
                    className="px-2 py-2 border-r"
                    style={{ width: column.width }}
                  >
                    {column.key === 'actions' ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={saveCreate}
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                          title="Lagre"
                          disabled={isLoading}
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelCreate}
                          className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                          title="Avbryt"
                          disabled={isLoading}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : column.key === 'itemNo' ? (
                      <input
                        type="text"
                        value={createData.itemNo || ''}
                        onChange={(e) => updateCreateField('itemNo', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        placeholder="Påkrevd"
                      />
                    ) : (
                      renderEditableCell(column, createData as Partial<ItemsDto>, updateCreateField as any)
                    )}
                  </td>
                ))}
              </tr>
            )}

            {/* Data rows */}
            {paginatedItems.map((item, index) => {
              const isSelected = selectedItemNos.includes(item.itemNo);
              const isEditing = editingItemNo === item.itemNo;
              const originalItem = items.find(i => i.itemNo === item.itemNo);

              return (
                <tr
                  key={item.itemNo}
                  className={`border-b transition-colors ${
                    isEditing ? 'bg-yellow-50' :
                    isSelected ? 'bg-blue-100' :
                    index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <td className="px-2 py-2 border-r text-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleCheckboxChange(item.itemNo, e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      disabled={isEditing}
                    />
                  </td>
                  {columns.map(column => (
                    <td
                      key={column.key}
                      className="px-2 py-2 border-r"
                      style={{ width: column.width }}
                    >
                      {column.key === 'actions' ? (
                        <div className="flex items-center gap-1">
                          {isEditing ? (
                            <>
                              <button
                                onClick={saveEdit}
                                className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                                title="Lagre"
                                disabled={isLoading}
                              >
                                <Save className="w-4 h-4" />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                                title="Avbryt"
                                disabled={isLoading}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => originalItem && startEdit(originalItem)}
                              className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                              title="Rediger"
                              disabled={isLoading}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ) : isEditing ? (
                        renderEditableCell(column, editingData, updateEditingField)
                      ) : (
                        renderCellValue(item, column)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {paginatedItems.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {totalRows === 0 ? 'Ingen varer funnet' : 'Ingen varer på denne siden'}
          </div>
        )}
      </div>

      {/* Bulk Update Modal */}
      {showBulkEditModal && selectedItemNos.length > 0 && (
        <ItemsBulkUpdateModal
          isOpen={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          selectedItemNos={selectedItemNos}
          companyNo={companyNo}
          onUpdateSuccess={handleUpdateSuccess}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};
